/**
 * Client-side bot detection for the PostHog cookieless capture floor.
 *
 * WHY THIS EXISTS (2026-07-19 data-quality incident):
 * Both frontends run PostHog in `cookieless_mode: "on_reject"`, so pending-consent
 * and declined visitors are captured cookielessly — the PostHog server derives a
 * stateful per-visitor identity by hashing the request IP + user-agent. For
 * JS-executing crawlers hitting SEO landing pages (e.g. /zawody/*, /koszty/*) that
 * hash cannot stabilise (rotating/absent IP+UA), so EVERY bot pageview mints a fresh
 * anonymous person. A single page (`/zawody/72-19-z`) showed 339 pageviews == 339
 * distinct persons — all www.google.com "organic", 99% Chrome, flat 24/7, 99% bounce,
 * $raw_user_agent=None. That inflated organic acquisition metrics with phantom people.
 *
 * PostHog's own built-in UA bot-blocklist can't catch these: cookieless mode strips
 * `$raw_user_agent` server-side, and the bots present a spoofed Chrome UA anyway. The
 * browser, however, still exposes `navigator.webdriver` and `navigator.userAgent` to
 * OUR JavaScript — so a conservative client-side check catches the JS-executing
 * crawlers that reach our `before_send` hook.
 *
 * DESIGN — deliberately conservative. A false positive silently DROPS a real
 * visitor's analytics, so this only fires on high-confidence signals:
 *   1. `navigator.webdriver === true`  — automation/headless (real Chrome reports false)
 *   2. a "HeadlessChrome" brand in `navigator.userAgentData.brands` — Chrome's
 *      UA-Client-Hints report the real binary even when the UA STRING is spoofed
 *      to look like normal Chrome (2026-07-28 wave: farms routinely spoof the
 *      string but forget the brands array)
 *   3. an explicit bot/crawler user-agent signature
 *   4. an empty/absent user-agent
 * It intentionally does NOT use fragile heuristics (missing plugins/languages, small
 * screens) — privacy browsers legitimately trip those, and dropping those users would
 * be worse than the bot noise. This narrows, it does not eliminate: a headless browser
 * that spoofs `webdriver=false`, a real Chrome UA, AND clean UA-CH brands is
 * indistinguishable client-side and remains a server-side filtering job.
 *
 * 2026-07-28 recurrence (same page, upgraded farm): a second wave on
 * `/zawody/72-19-z` (~685 minted persons, Jul 16–23) presented `webdriver` unset, a
 * clean Chrome 149 UA string, and passed this floor entirely. Its only reliable
 * discriminator (unresolvable GeoIP) exists server-side, so the durable exclusion
 * for that fingerprint lives in PostHog: dynamic cohort 193611 ("Bot farm —
 * headless Chrome google-referrer wave") wired into the project's test-account
 * filters. If a new wave appears, extend the COHORT first — it needs no deploy and
 * cleans history retroactively — and only extend this module for signals that are
 * near-zero-false-positive client-side.
 */

/**
 * Bot / automation / crawler user-agent signatures, matched case-insensitively.
 *
 * TOKEN-AWARE, NOT bare substring (2026-07-19 review — Codex P2). A bare `bot`
 * substring false-positives on real device brands: `CUBOT` handsets ship a
 * user-agent containing "bot", so `"cubot".includes("bot")` would flag real
 * Android visitors and drop their analytics — the exact failure the conservative
 * design forbids. Instead:
 *   - The generic crawler family is matched as `bot/<version>` (the slash-version
 *     product form every well-behaved crawler uses: `Googlebot/2.1`, `bingbot/2.0`,
 *     `YandexBot/3.0`), which cannot match `CUBOT_X30` / `CUBOT NOTE 20`.
 *   - Named crawlers that omit the slash form are listed explicitly.
 *   - Remaining tokens (`crawl`, `spider`, `headless`, HTTP libs, automation
 *     frameworks) are unambiguous enough not to appear in a real handset UA.
 */
const BOT_UA_REGEX = new RegExp(
  [
    'bot/', // Googlebot/2.1, bingbot/2.0, YandexBot/3.0, GPTBot/1.0, ClaudeBot/1.0, …
    'adsbot-google',
    'mediapartners-google',
    'google-inspectiontool',
    'googleother',
    'storebot-google',
    'facebookexternalhit',
    'facebookcatalog',
    'crawler',
    'crawl',
    'spider', // baiduspider, bytespider, …
    'slurp',
    'headless', // HeadlessChrome — a common signal for the JS-executing crawlers we saw
    'phantomjs',
    'phantom',
    'puppeteer',
    'playwright',
    'selenium',
    'webdriver',
    'scrapy',
    'python-requests',
    'python-urllib',
    'aiohttp',
    'go-http-client',
    'okhttp',
    'curl/',
    'wget/',
    'libwww',
    'httpclient',
    'lighthouse',
    'pagespeed',
    'gtmetrix',
    'pingdom',
    'uptimerobot',
    'embedly',
    'prerender',
  ]
    // Escape regex metacharacters (only `/` here, but keep it safe if the list grows).
    .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'i'
);

interface BotNavigatorLike {
  webdriver?: boolean;
  userAgent?: string;
  /** UA-Client-Hints (Chromium only) — brands betray HeadlessChrome even under a spoofed UA string. */
  userAgentData?: {
    brands?: Array<{ brand?: string; version?: string }>;
  };
}

/** Resolve `navigator` without assuming a DOM (SSR-safe). */
function getNavigator(): BotNavigatorLike | undefined {
  return (globalThis as typeof globalThis & { navigator?: BotNavigatorLike }).navigator;
}

/**
 * True when the current client is a high-confidence bot/automation agent.
 *
 * SSR-safe: returns `false` when `navigator` is unavailable (no client to judge —
 * fail open so server-rendered / test contexts are never mistaken for bots).
 *
 * @param navigatorOverride injectable navigator for tests
 */
export function isLikelyBot(navigatorOverride?: BotNavigatorLike): boolean {
  const nav = navigatorOverride ?? getNavigator();
  if (!nav) return false;

  // 1. Automation flag — the strongest, near-zero-false-positive signal.
  if (nav.webdriver === true) return true;

  // 2. UA-Client-Hints brands — headless Chromium reports a "HeadlessChrome" brand
  //    even when the UA string is spoofed to plain Chrome. Real browsers never
  //    carry a Headless brand, so this stays near-zero-false-positive.
  const brands = nav.userAgentData?.brands;
  if (Array.isArray(brands) && brands.some((b) => /headless/i.test(b?.brand ?? ''))) {
    return true;
  }

  const ua = nav.userAgent;

  // 3. Missing/empty user-agent — real browsers always send one; headless/scripted
  //    clients frequently omit it.
  if (typeof ua !== 'string' || ua.trim() === '') return true;

  // 4. Explicit bot/crawler signature (token-aware — see BOT_UA_REGEX).
  return BOT_UA_REGEX.test(ua);
}
